import React from 'react';
import { isEmpty } from 'lodash';
import I18n from 'i18n-js';
import classNames from 'classnames';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import AdTag from '@reverbdotcom/commons/src/components/ads/ad_tag';
import { MParticleEventName, MParticleEvent } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { useExpEnabled, useUser } from '@reverbdotcom/commons/src/user_hooks';
import experiments from '@reverbdotcom/commons/src/experiments';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import { RCIcon } from '@reverbdotcom/commons/src/rc/RCIcon';
import { RCTextWithIcon } from '@reverbdotcom/commons/src/rc/RCTextWithIcon';
import AngleRightSvg from '@reverbdotcom/commons/design-system/icons/core/angle-right.svg?component';
import ChevronRightSvg from '@reverbdotcom/commons/design-system/icons/core/chevron-right.svg?component';

const COMPONENT_NAME = 'PromotionalBannerTop';

export interface ISiteBanner {
  id: string;
  heading: string;
  subheading: string;
  callToAction: string;
  downtime: boolean;
  url: {
    href: string;
  };
  mobileWebUrl: {
    href: string;
  };
  contentSponsorship: boolean;
}

interface PromotionalBannerTopProps {
  siteBanner: ISiteBanner;
}

function buildClickEvent(siteBanner: ISiteBanner, user): MParticleEvent {
  return {
    eventName: MParticleEventName.SiteBanner,
    componentName: COMPONENT_NAME,
    entityId: siteBanner.id,
    isAd: !!siteBanner.contentSponsorship,
    experiments: formatExperimentsForEventAttributes(user, [experiments.PROMOTIONAL_BANNER_REFRESH]),
  };
}

function SponsorshipContent() {
  return (
    <span className="promotional-banner-top__info__ad">
      <AdTag inline small />
    </span>
  );
}

function MobilePromotionalBanner({ siteBanner }: PromotionalBannerTopProps) {
  const user = useUser();
  const classes = classNames(
    'promotional-banner-top',
    'd-none',
    'mobile-d-block',
  );

  return (
    <div className={classes}>
      <CoreLink
        to={siteBanner.mobileWebUrl.href}
        clickEvent={buildClickEvent(siteBanner, user)}
      >
        <aside className="promotional-banner-top__inner">
          <div className="promotional-banner-top__info">
            <b>{siteBanner.heading}</b>
            <span className="promotional-banner-top__info__subheading">
              {siteBanner.subheading}
            </span>
            {siteBanner.contentSponsorship && <SponsorshipContent />}
          </div>
          <div className="promotional-banner-top__action">
            {I18n.t('discovery.siteBanner.sale.getAppAnd', { call_to_action: I18n.t('discovery.siteBanner.sale.viewSale') })}
          </div>
        </aside>
      </CoreLink>
    </div>
  );
}

function DownTimePromotionalBanner({ siteBanner }: PromotionalBannerTopProps) {
  const classes = classNames(
    'promotional-banner-top',
    'promotional-banner-top--downtime',
  );
  return (
    <div className={classes}>
      <aside className="promotional-banner-top__inner">
        <div className="promotional-banner-top__info">
          <b>{siteBanner.heading}</b>
          {siteBanner.subheading}
        </div>
      </aside>
    </div>
  );
}

function ExperimentBanner({ siteBanner }: PromotionalBannerTopProps) {
  const user = useUser();
  const classes = classNames(
    'promotional-banner-top',
    'promotional-banner-top--new',
    { 'promotional-banner-top--ad': !!siteBanner.contentSponsorship },
  );

  return (
    <div className={classes}>
      <CoreLink
        to={user?.deviceName === 'mobile-browser' ? siteBanner.mobileWebUrl.href : siteBanner.url.href}
        clickEvent={buildClickEvent(siteBanner, user)}
      >
        <aside className="promotional-banner-top__inner">
          <p className="promotional-banner-top__content">
            <span className="promotional-banner-top__subheading">
              <RCTextWithIcon svgComponent={ChevronRightSvg}>
                {siteBanner.subheading}
              </RCTextWithIcon>
            </span>
            <span className="promotional-banner-top__heading">
              {siteBanner.heading}
            </span>
            <span className="promotional-banner-top__action">
              {siteBanner.callToAction}
            </span>
          </p>
          {siteBanner.contentSponsorship &&
            <div className="promotional-banner-top__ad">
              <AdTag small />
            </div>
          }
          <div className="promotional-banner-top__icon">
            <RCIcon svgComponent={AngleRightSvg} />
          </div>
        </aside>
      </CoreLink>
    </div>
  );
}

export function PromotionalBannerTop(props: PromotionalBannerTopProps) {
  const user = useUser();
  const renderExperimentBanner = useExpEnabled(experiments.PROMOTIONAL_BANNER_REFRESH);
  const { siteBanner } = props;
  if (!siteBanner || isEmpty(siteBanner)) return null;

  const mobileWebUrl = siteBanner.url.href !== siteBanner.mobileWebUrl.href;
  const classes = classNames(
    'promotional-banner-top',
    { 'mobile-d-none': mobileWebUrl },
  );

  if (siteBanner.downtime) {
    return <DownTimePromotionalBanner siteBanner={siteBanner} />;
  }

  if (renderExperimentBanner) {
    return <ExperimentBanner siteBanner={siteBanner} />;
  }

  return (
    <>
      <div className={classes}>
        <CoreLink
          to={siteBanner.url.href}
          clickEvent={buildClickEvent(siteBanner, user)}
        >
          <aside className="promotional-banner-top__inner">
            <div className="promotional-banner-top__info">
              <b>{siteBanner.heading}</b>
              <span className="promotional-banner-top__info__subheading">
                {siteBanner.subheading}
              </span>
              {siteBanner.contentSponsorship && <SponsorshipContent />}
            </div>
            <div className="promotional-banner-top__action">
              {siteBanner.callToAction}
            </div>
          </aside>
        </CoreLink>
      </div>
      {mobileWebUrl && <MobilePromotionalBanner siteBanner={siteBanner} />}
    </>
  );
}

export default PromotionalBannerTop;
